import React from 'react';
import { Link } from 'react-router-dom';
import { RichTextViewer, TextButton, Typography } from 'seagull';
import { UserRole } from "../../../../store/store.model";
import { routes } from "../../../../constants";
import { netTermsToString, ordinal } from "../../../../infra/utils";
import { getUnitName, pluralizeUnitName } from "../../../../utils/unit-name.utils";
import { getURLWithFullPageInvoice } from "../../../FullPageInvoice/FullPageInvoice.utils";
import { activityLogModel, billingDayOfMonthModel, relationshipModel, serviceModel } from "../../../../models";
import { utils } from "../../../../utils";
import { PaymentMethodTypes } from "../../../../models/payment-method";
import { getAllowManualPaymentsChangeText, getAmendmentsApprovalMethodChangeBullet, getCoverCCFeesChangeText } from "../../../../business-logic/amendmentsLogic";
export function getActivityLogItemContent(config) {
  const entry = config.entry;

  switch (entry.type) {
    case activityLogModel.Type.Relationship:
      return getAgreementContent(entry, config);

    case activityLogModel.Type.Charge:
      return getChargeContent(entry, config);

    case activityLogModel.Type.Invoice:
      return getInvoiceContent(entry, config);

    case activityLogModel.Type.Service:
      return getServiceContent(entry);

    default:
      warnForUnrecognizedEntry(entry);
      return null;
  }
}

function warnForUnrecognizedEntry(data) {
  console.warn('Unrecognized activity-log entry', data);
}

function getAgreementContent(entry, config) {
  const vendorName = activityLogModel.vendorName(entry) || '';
  const customer = activityLogModel.customer(entry) || '';
  const customerName = activityLogModel.customerName(entry) || '';
  const paymentMethod = activityLogModel.agreementPaymentMethod(entry);
  const channel = activityLogModel.agreementChannel(entry);

  switch (activityLogModel.agreementState(entry)) {
    case activityLogModel.AgreementState.Sent:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, /*#__PURE__*/React.createElement(AgreementLink, {
        config: config,
        uppercase: true
      }), " sent to ", customerName, ".");

    case activityLogModel.AgreementState.Reviewed:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, /*#__PURE__*/React.createElement(AgreementLink, {
        config: config,
        uppercase: true
      }), " was reviewed by ", customerName, ".");

    case activityLogModel.AgreementState.MicroDepositStarted:
    case activityLogModel.AgreementState.MicroDeposit:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, customerName, " initiated a micro-deposit verification.");

    case activityLogModel.AgreementState.MicroDepositStartedV2:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Micro-deposit verification process for bank account (", (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") has been initiated.");

    case activityLogModel.AgreementState.MicroDepositFailed:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Micro-deposit verification failed.");

    case activityLogModel.AgreementState.MicroDepositFailedV2:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Bank account (", (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") failed verification.", ' ', /*#__PURE__*/React.createElement(TextButton, {
        as: Link,
        to: routes.paymentMethods(config.relationship.id),
        "data-testid": config.testid && "".concat(config.testid, "-pm-link"),
        underline: "none"
      }, "Manage payment details"), ".");

    case activityLogModel.AgreementState.MicroDepositVerified:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Micro-deposit was verified by ", customerName || vendorName, ".");

    case activityLogModel.AgreementState.MicroDepositVerifiedV2:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Bank account (", (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") was verified and connected successfully.");

    case activityLogModel.AgreementState.PaymentMethod:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, customerName || vendorName, " added a payment method.");

    case activityLogModel.AgreementState.Approved:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, /*#__PURE__*/React.createElement(AgreementLink, {
        config: config,
        uppercase: true
      }), " approved by ", customerName, ".");

    case activityLogModel.AgreementState.Terminated:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, /*#__PURE__*/React.createElement(AgreementLink, {
        config: config,
        uppercase: true
      }), " terminated by ", vendorName, ".");

    case activityLogModel.AgreementState.Changed:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, /*#__PURE__*/React.createElement(AgreementLink, {
        config: config,
        uppercase: true
      }), " was edited by ", vendorName, " and now includes the following: ", relationshipUtils.getNetTerms(activityLogModel.agreementChangesNetTerms(entry)));

    case activityLogModel.AgreementState.ChangesApproved:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "The latest amendments have been approved by ", customerName, ".");

    case activityLogModel.AgreementState.ChangesAutoApproved:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "As per the agreement terms, the latest amendments are effective as of today.");

    case activityLogModel.AgreementState.StoppedAutoApproval:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, customerName, " declined the auto-approval of the latest amendments.");

    case activityLogModel.AgreementState.SigningReminderSent:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Anchor sent an automatic ", channel, " reminder to ", customerName, ", to approve the proposal.");

    case activityLogModel.AgreementState.SigningReminderSentManual:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, vendorName, " sent an ", channel, " reminder to ", customerName, ", to approve the proposal.");

    case activityLogModel.AgreementState.MicroDepositReminderSent:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Anchor sent an automatic micro deposit verification reminder to ", customerName, ".");

    case activityLogModel.AgreementState.PaymentMethodChanged:
      {
        if (!paymentMethod || !customer) return null;
        const pmText = paymentMethod.type === PaymentMethodTypes.CreditCard ? 'Credit card' : 'Bank account';
        return /*#__PURE__*/React.createElement("div", {
          "data-testid": config.testid
        }, "The ", /*#__PURE__*/React.createElement(AgreementLink, {
          config: config
        }), " has been updated with a new payment method - ", pmText, " ending in ", paymentMethod.last4, " by ", customer.name, customer.role === UserRole.Accountant ? " (".concat(customer.email, ")") : '', ".");
      }

    case activityLogModel.AgreementState.SignedExternally:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Existing agreement was uploaded and authorized for billing on Anchor by ", vendorName, ".");

    case activityLogModel.AgreementState.RequestedPaymentMethod:
      {
        if (!customer) return null;
        return /*#__PURE__*/React.createElement("div", {
          "data-testid": config.testid
        }, "A request to add a payment method has been sent to ".concat(customer.name, " (").concat(customer.email, ")"));
      }

    case activityLogModel.AgreementState.LegalTermsUpdated:
      {
        return /*#__PURE__*/React.createElement("div", {
          "data-testid": config.testid
        }, "Terms & Conditions have been updated.");
      }

    case activityLogModel.AgreementState.AmendmentsApprovalMethodUpdated:
      {
        const approvalMethodChanges = activityLogModel.agreementChangesApprovalMethod(entry);
        if (!approvalMethodChanges) return null;
        const bullet = getAmendmentsApprovalMethodChangeBullet(approvalMethodChanges.before, approvalMethodChanges.after);
        return bullet && /*#__PURE__*/React.createElement("div", {
          "data-testid": config.testid
        }, bullet.text, vendorName ? " by ".concat(vendorName) : '', ".");
      }

    case activityLogModel.AgreementState.TitleUpdated:
      {
        const changesTitle = activityLogModel.agreementChangesTitle(entry);
        return changesTitle ? /*#__PURE__*/React.createElement("div", {
          "data-testid": config.testid
        }, vendorName, " renamed this ", /*#__PURE__*/React.createElement(AgreementLink, {
          config: config
        }), " \"", changesTitle.after, "\"") : null;
      }

    case activityLogModel.AgreementState.ManualPaymentsUpdated:
      {
        const changesManualPayments = activityLogModel.agreementChangesManualPayments(entry);
        if (!changesManualPayments) return null;
        const {
          before,
          after
        } = changesManualPayments;
        return /*#__PURE__*/React.createElement("div", {
          "data-testid": config.testid
        }, getAllowManualPaymentsChangeText(before, after), " by ", vendorName, ".");
      }

    case activityLogModel.AgreementState.CoverCreditCardFeesUpdated:
      {
        const changesCoverCCFees = activityLogModel.agreementChangesCoverCCFees(entry);
        if (!changesCoverCCFees) return null;
        const changeText = getCoverCCFeesChangeText(changesCoverCCFees.before, changesCoverCCFees.after, relationshipModel.vendorName(config.relationship), relationshipModel.customerName(config.relationship));
        return /*#__PURE__*/React.createElement("div", {
          "data-testid": config.testid
        }, changeText, " by ", vendorName);
      }

    default:
      warnForUnrecognizedEntry(entry);
      return null;
  }
}

function getChargeContent(entry, config) {
  const charge = activityLogModel.charge(entry);
  const vendorName = activityLogModel.vendorName(entry) || '';
  const customerName = activityLogModel.customerName(entry) || '';
  const chargeNote = activityLogModel.chargeNote(entry);
  const formattedPrice = activityLogModel.chargePrice(entry).display();

  switch (activityLogModel.chargeState(entry)) {
    case activityLogModel.ChargeState.Created:
      {
        const noteText = chargeNote ? ", with the following note: ".concat(chargeNote) : '';
        return /*#__PURE__*/React.createElement("div", {
          "data-testid": config.testid
        }, "A new charge, ".concat(charge.name, ", with a price of ").concat(formattedPrice, " was added by ").concat(vendorName).concat(noteText, "."));
      }

    case activityLogModel.ChargeState.Approved:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "A charge, ".concat(charge.name, ", with a price of ").concat(formattedPrice, ", was approved by ").concat(customerName, "."));

    case activityLogModel.ChargeState.Canceled:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "The charge, ".concat(charge.name, ", with a price of ").concat(formattedPrice, ", was canceled by ").concat(vendorName, "."));

    default:
      warnForUnrecognizedEntry(entry);
      return null;
  }
}

function getInvoiceContent(entry, config) {
  var _entry$data$paymentSt;

  const {
    relationship,
    testid,
    logBI
  } = config;
  const relationshipId = relationship.id;
  const vendorName = activityLogModel.vendorName(entry) || '';
  const customerName = activityLogModel.customerName(entry) || '';
  const invoiceId = activityLogModel.invoiceId(entry);
  const invoiceFormattedNumber = activityLogModel.invoiceFormattedNumber(entry);
  const totalAmount = activityLogModel.invoiceTotalAmount(entry);
  const totalAmountDisplay = totalAmount.display();
  const invoiceLink = /*#__PURE__*/React.createElement(TextButton, {
    as: Link,
    to: getURLWithFullPageInvoice({
      invoiceId,
      relationshipId
    }),
    onClick: () => logBI(10575, {
      invoiceId
    }),
    underline: "none",
    "data-testid": testid && "".concat(testid, "-invoice-link")
  }, /*#__PURE__*/React.createElement("span", null, invoiceFormattedNumber));

  switch (activityLogModel.invoiceState(entry)) {
    case activityLogModel.InvoiceState.Voided:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was voided by ", vendorName, ".");

    case activityLogModel.InvoiceState.PaymentStoppedOLD:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was disputed by ", customerName, ".");

    case activityLogModel.InvoiceState.PaymentStopped:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "A payment for invoice ", invoiceLink, " was stopped by ", customerName, " from", ' ', relationshipModel.customerName(relationship), " with the following note: \"", ((_entry$data$paymentSt = entry.data.paymentStopped) === null || _entry$data$paymentSt === void 0 ? void 0 : _entry$data$paymentSt.reason) || '', "\".");

    case activityLogModel.InvoiceState.PaymentResumed:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "A payment for invoice ", invoiceLink, " was resumed by ", customerName, " from", ' ', relationshipModel.customerName(relationship), ".");

    case activityLogModel.InvoiceState.Completed:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was finalized (added hours/units) by ", vendorName, ".");

    case activityLogModel.InvoiceState.IssuedIncomplete:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was issued but has missing quantity/hours.");

    case activityLogModel.InvoiceState.Issued:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was issued.");

    case activityLogModel.InvoiceState.PaymentFailed:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, ": Payment failed.");

    case activityLogModel.InvoiceState.PaymentRetried:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was charged manually.");

    case activityLogModel.InvoiceState.Paid:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was paid.");

    case activityLogModel.InvoiceState.PaidExternally:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was marked as paid.");

    case activityLogModel.InvoiceState.DisputeResolved:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "A payment for invoice ", invoiceLink, " has been stopped by ", customerName, " from", ' ', relationshipModel.customerName(relationship), ".");

    case activityLogModel.InvoiceState.Approved:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " was approved by ", customerName, ".");

    case activityLogModel.InvoiceState.Resent:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " resent by ", vendorName, ".");

    case activityLogModel.InvoiceState.PaymentConfirmed:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " payment was confirmed.");

    case activityLogModel.InvoiceState.PaymentProcessing:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " payment was processed.");

    case activityLogModel.InvoiceState.Refunded:
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invoice ", invoiceLink, " for ", totalAmountDisplay, " has been refunded.");

    case activityLogModel.InvoiceState.PaymentDisputed:
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": config.testid
      }, "Invoice ", invoiceLink, ": Payment disputed.");

    default:
      warnForUnrecognizedEntry(entry);
      return null;
  }
}

function getServiceContent(entry) {
  const state = activityLogModel.serviceState(entry);
  const vendorName = activityLogModel.vendorName(entry) || '';
  const customerName = activityLogModel.customerName(entry) || '';
  const service = activityLogModel.service(entry);

  switch (state) {
    case activityLogModel.ServiceState.Approved:
      return "The addition of ".concat(service.name, " service was approved by ").concat(customerName, ".");

    case activityLogModel.ServiceState.ChangesApproved:
      return "An amendment to ".concat(service.name, " service was approved by ").concat(customerName, ".");

    case activityLogModel.ServiceState.ChangesAutoApproved:
      return "As per the agreement terms, the amendments to ".concat(service.name, " service are effective as of today.");

    case activityLogModel.ServiceState.Resumed:
      return "".concat(vendorName, " resumed the ").concat(service.name, " service.");

    case activityLogModel.ServiceState.Paused:
      return "".concat(vendorName, " paused the ").concat(service.name, " service.");

    case activityLogModel.ServiceState.Created:
      {
        return [/*#__PURE__*/React.createElement(React.Fragment, null, "A new service was added by ", vendorName, "."), "The ".concat(service.name, " service was added and includes the following:"), serviceUtils.getDescription(service), serviceUtils.getPricePerUnitText(service), serviceUtils.getPriceRange(service), serviceUtils.getDiscountText(service), serviceUtils.getCap(service), serviceUtils.getRecurrenceText(service), serviceUtils.getNote(service)].filter(Boolean);
      }

    case activityLogModel.ServiceState.Changed:
      {
        return [/*#__PURE__*/React.createElement(React.Fragment, null, "An existing service was edited by ", vendorName, "."), "The ".concat(service.name, " service was edited and now includes the following:"), serviceUtils.getDescription(service), serviceUtils.getPricePerUnitText(service), serviceUtils.getDiscountText(service), serviceUtils.getCap(service), serviceUtils.getRecurrenceText(service), serviceUtils.getNote(service)].filter(Boolean);
      }

    case activityLogModel.ServiceState.Terminated:
      return "Service ".concat(service.name, " has been stopped by ").concat(vendorName);

    case activityLogModel.ServiceState.Canceled:
      return "Service ".concat(service.name, " has been canceled by ").concat(vendorName);

    case activityLogModel.ServiceState.BillCommandDeliverablesPendingApproval:
      return "Delivery of ".concat(service.name, " was sent by ").concat(vendorName, ".");

    case activityLogModel.ServiceState.BillCommandDeliverablesApproved:
      return "Delivery of ".concat(service.name, " was approved by ").concat(customerName, ".");

    case activityLogModel.ServiceState.BillCommandDeliverablesRejected:
      return "Delivery of ".concat(service.name, " was rejected by ").concat(customerName, ".");

    case activityLogModel.ServiceState.BillCommandDeliverablesWithdrawn:
      return "Delivery of ".concat(service.name, " was withdrawn by ").concat(vendorName, ".");

    case activityLogModel.ServiceState.ChangesDiscarded:
      return "Amendment of ".concat(service.name, " was discarded by ").concat(vendorName, ".");

    default:
      warnForUnrecognizedEntry(entry);
      return null;
  }
}

const relationshipUtils = {
  getNetTerms(netTerms) {
    if (netTerms || netTerms === 0) {
      return /*#__PURE__*/React.createElement("div", null, "Payment terms: ", netTermsToString(netTerms));
    }

    return '';
  }

};
const serviceUtils = {
  getDescription(service) {
    if (!service.changes || !service.changes.description) {
      return '';
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, "Description:"), /*#__PURE__*/React.createElement(RichTextViewer, {
      value: service.changes.description
    }));
  },

  getNote(service) {
    return service.note ? /*#__PURE__*/React.createElement(MultiLineText, {
      text: service.note,
      prefix: "Note: "
    }) : '';
  },

  getRecurrenceText(service) {
    if (!service.changes) {
      return '';
    }

    const {
      billing
    } = service.changes;
    const isRecurring = !!billing && serviceModel.isRecurring(billing);
    const recurrenceText = isRecurring ? " with ".concat(serviceModel.recurrencePeriod(billing), " billing") : '';
    const dayOfWeek = billing ? serviceModel.billingDayOfWeek({
      billing
    }) : null;
    const dayOfMonth = billing ? serviceModel.billingDayOfMonth({
      billing
    }) : null;
    const dayText = dayOfWeek ? " (on ".concat(dayOfWeek, ")") : dayOfMonth ? " (on the ".concat(billingDayOfMonthModel.isLastDayOfMonth(dayOfMonth) ? 'last day' : ordinal(dayOfMonth.day), " of each month)") : '';
    return "The service will be ".concat(isRecurring ? 'an ongoing' : 'a one-time', " service").concat(recurrenceText).concat(dayText, ".");
  },

  getPricePerUnitText(service) {
    const {
      cost
    } = service.changes || {};
    const price = cost && serviceModel.price({
      cost
    });

    if (price) {
      const formattedPrice = utils.formatDollarAmount(price);

      if (serviceModel.isVariablePrice(cost)) {
        return "Price: ".concat(formattedPrice, "/").concat(getUnitName(serviceModel.unitName({
          cost
        })));
      } else if (serviceModel.isRangePrice(cost)) {
        const minPrice = serviceModel.minPrice({
          cost
        });
        return "Price: ".concat(minPrice ? utils.formatDollarAmount(minPrice) : '0', "-").concat(formattedPrice, " range price");
      } else {
        return "Price: ".concat(formattedPrice, " fixed price");
      }
    } else {
      return '';
    }
  },

  getPriceRange(service) {
    const {
      cost
    } = service.changes || {};
    const isRangePrice = !!cost && serviceModel.isRangePrice(cost);

    if (isRangePrice) {
      const {
        minPrice,
        maxPrice
      } = cost;

      if (minPrice) {
        return "Pricing: From $".concat(minPrice, " to $").concat(maxPrice);
      } else {
        return "Pricing: Up to $".concat(maxPrice);
      }
    }

    return '';
  },

  getDiscountText(service) {
    const {
      cost
    } = service.changes || {};
    const discount = cost && serviceModel.discount({
      cost
    });
    return discount ? "Discount of ".concat(utils.formatDiscount(discount)) : '';
  },

  getCap(service) {
    const {
      cost
    } = service.changes || {};
    const unitCap = cost && serviceModel.unitCap({
      cost
    });
    return unitCap ? "Preapproved: ".concat(utils.formatNumberWithCommas(unitCap), " ").concat(pluralizeUnitName(unitCap, serviceModel.unitName({
      cost
    }))) : '';
  }

};

function MultiLineText(props) {
  // We support multi-line free-text with \n separators encoded in the string.
  const lines = props.text.trim().split('\n');
  return /*#__PURE__*/React.createElement(React.Fragment, null, lines.map((line, idx) => /*#__PURE__*/React.createElement(Typography, {
    key: idx
  }, idx === 0 && props.prefix, line)));
}

function AgreementLink(props) {
  const {
    uppercase,
    config: {
      relationship,
      logBI,
      testid
    }
  } = props;
  return /*#__PURE__*/React.createElement(TextButton, {
    as: Link,
    to: routes.agreement(relationship.id),
    onClick: () => logBI(10576),
    "data-testid": testid && "".concat(testid, "-agreement-link"),
    underline: "none"
  }, uppercase ? 'Agreement' : 'agreement');
}